import { ReactiveVar } from '@apollo/client';
import { HStack } from '@starrez/starryui9';
import { SortAsc } from '@starryui10/images/icons/SortAsc';
import { SortDesc } from '@starryui10/images/icons/SortDesc';
import { strings } from '@starryui10/localisation';
import { color } from '@starryui10/theme/color';
import { Text } from '@starryui10/typography/Text';
import { ColDef, IHeaderParams } from 'ag-grid-community';
import React, { useState } from 'react';
import styled from 'styled-components';
export const LinkHeader = ({
    displayName,
    column,
    onClick,
    orderKey,
    cacheFunc,
    columnFilterButtonComponent: ColumnFilterButton,
}: IHeaderParams & {
    // Please note that orderKey and columnFilterButtonComponent props type are passed in via useHeader, hence the any here
    cacheFunc: ReactiveVar<global.GridSortingVars>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onClick: (orderKey: any, order: global.SortOrderEnum) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    orderKey: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    columnFilterButtonComponent?: (props: any) => JSX.Element;
}) => {
    const data = cacheFunc();

    const [sortingOrder, setSortingOrder] = useState<global.SortOrderEnum>('ASC');

    const handleOnClick = () => {
        let order: global.SortOrderEnum;

        if (data.field === orderKey) {
            // swap the orders
            if (data.order === 'DESC') {
                order = 'ASC';
            } else {
                order = 'DESC';
            }
        } else {
            // use the order we have already
            order = sortingOrder;
        }

        setSortingOrder(order);
        cacheFunc({ field: orderKey, order });
        onClick(orderKey, order);
    };

    return (
        <HStack columns="auto 32px 32px" height="100%">
            <DispayNameButton type="button" onClick={handleOnClick}>
                <Text variant="small.bold" color={'inherit'}>
                    {displayName}
                </Text>
            </DispayNameButton>
            <OrderButton
                display={data.field === orderKey}
                displayName={displayName}
                handleOnClick={handleOnClick}
                icon={data.field === orderKey ? data.order : sortingOrder}
            />
            {ColumnFilterButton && <ColumnFilterButton displayName={displayName} field={column.getColId()} />}
        </HStack>
    );
};

const OrderButton = ({
    display,
    displayName,
    icon,
    handleOnClick,
}: {
    display: boolean;
    displayName: string;
    icon: global.SortOrderEnum;
    handleOnClick: () => void;
}) => {
    const orderLabel = icon === 'ASC' ? strings.HeaderCell_Label_Asc : strings.HeaderCell_Label_Desc;
    const title = strings
        .formatString(display ? strings.HeaderCell_Label_HasOrder : strings.HeaderCell_Label_NoOrder, displayName, orderLabel)
        .toString();

    return (
        <StyledButton type="button" display={display} onClick={handleOnClick} aria-label={title} title={title}>
            {icon === 'ASC' ? <SortAsc height="16px" width="16px" /> : <SortDesc height="16px" width="16px" />}
        </StyledButton>
    );
};

export const DispayNameButton = styled.button`
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 0;
    margin: 0;
    cursor: pointer !important;
    text-transform: uppercase;
    background: none;
    border: none;
    color: ${color.Grey.G70};

    &:hover {
        color: ${color.Blue.B80};
    }

    * {
        cursor: pointer !important;
    }
`;

export const StyledButton = styled.button<{ display: boolean }>`
    background: none;
    color: ${color.Grey.G70};
    border: none;
    height: 32px;
    width: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    opacity: ${(props) => (props.display ? '1' : '0')};
    transition: opacity ease-in-out 0.2s;
    cursor: pointer;

    * {
        cursor: pointer !important;
    }

    &:hover {
        background: ${color.Blue.B20};
        color: ${color.Blue.B80};
        opacity: 1;
    }
`;

export function useHeaderCell<T, K>(
    cacheFunc: ReactiveVar<global.GridSortingVars>,
    onClick: (orderKey: T, order: global.SortOrderEnum) => void,
    columnFilterButtonComponent?: (props: K) => JSX.Element
): (orderKey: T) => ColDef {
    return (orderKey: T) => ({
        headerComponentFramework: LinkHeader,
        headerComponentParams: {
            onClick,
            orderKey,
            cacheFunc,
            columnFilterButtonComponent,
        },
    });
}
